import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { NavigationItem } from '@app/core/types/navigation.interface';
import { AuthenticationService } from '@app/core/auth';
import { MenuStateService } from '@app/core/services/menu-state.service';
import { MENU_ICON_MAP, MENU_INTERNAL_ROUTES } from '@app/core/const';
import { TranslatePipe } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-menu-item',
  standalone: true,
  imports: [RouterModule, MenuItemVerticalComponent, TranslatePipe, MatIconModule],
  template: `
    @if (item(); as menuItem) {
      @if (!menuItem.hidden) {
        <li
          class="nav-item"
          [class.coded-hasmenu]="level() === 0"
          [class.nav-item--has-children]="hasChildren()"
          [class.nav-item--open]="isOpen()"
          [class.active]="isActive()"
          [attr.data-menu-id]="menuId()"
          [matTooltip]="tooltipText()"
        >
          <a
            class="nav-link"
            [class.disabled]="!isEnabled()"
            [routerLink]="!hasChildren() && isTrulyInternal() ? internalRoutes[menuItem.id] : null"
            [attr.href]="!hasChildren() && !isTrulyInternal() ? (isEnabled() ? menuItem.url : null) : null"
            [attr.target]="menuItem.target ? '_blank' : null"
            (click)="handleClick($event)"
          >
            <div class="menu-item__content">
              <div class="menu-item__content__element">
                <mat-icon class="menu-item__icon">
                  {{ !isEnabled() ? 'lock' : iconName() && level() === 0 ? iconName() : 'keyboard_double_arrow_right' }}
                </mat-icon>
                <span class="coded-mtext">{{ menuItem.title | translate }}</span>
              </div>

              @if (menuItem.addUrl && isEnabled()) {
                <div class="menu-item__content__dashed">
                  <mat-icon class="menu-item__icon__add" (click)="onAddClick($event)">add</mat-icon>
                </div>
              }

              @if (hasChildren()) {
                <mat-icon class="menu-item__icon__chevron">
                  {{ isOpen() ? 'expand_more' : 'chevron_right' }}
                </mat-icon>
              }
            </div>
          </a>

          @if (hasChildren() && isOpen()) {
            <ul class="nav-submenu">
              @for (childItem of menuItem.children; track childItem.id) {
                <app-menu-item [item]="childItem" [level]="level() + 1" />
              }
            </ul>
          }
        </li>
      }
    }
  `,
  styles: [
    `
      .menu-item {
        &__link {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0.75rem 1.25rem;
          position: relative;
          cursor: pointer;
          transition: all 0.3s ease;

          &--active {
            color: var(--primary-500);
            font-weight: 500;

            .menu-collapse__icon,
            .expand-icon {
              color: var(--primary-500);
              font-size: 24px;
            }
          }

          &[aria-disabled='true'] {
            opacity: 0.6;
            pointer-events: none;
          }

          &:focus-visible {
            outline: 2px dashed var(--primary-500);
            outline-offset: -2px;
          }
        }

        &__content {
          display: flex;
          align-items: center;
          gap: 8px;
          justify-content: space-between;
        }

        &__content__dashed {
          display: flex;
          align-items: center;
          gap: 8px;
          justify-content: space-between;
          border-left: 2px dashed;
        }

        &__content__element {
          display: grid;
          grid-template-columns: 32px 1fr;
          column-gap: 13px;
          align-items: center;
        }

        &__icon {
          display: flex;
          align-items: center;
          font-size: 24px;
          margin-left: clamp(0.5rem, 0.25rem + 0.5vw, 1rem);
          transition: color 0.3s ease;
          font-weight: bold;
        }

        &__icon__add {
          display: flex;
          align-items: center;
          font-size: 18px;
          margin-left: clamp(0.5rem, 0.25rem + 0.5vw, 1rem);
          transition: color 0.3s ease;
          font-weight: bold;
          z-index: 1;
        }

        &__text {
          display: flex;
          align-items: center;
          font-size: clamp(0.875rem, 0.8rem + 0.25vw, 1rem);
          margin-left: clamp(0.5rem, 0.25rem + 0.5vw, 1rem);
          transition: color 0.3s ease;
          font-weight: bold;
        }

        &__submenu {
          list-style: none;
          padding-left: 1rem;
          margin: 0;
          overflow: hidden;
        }
      }

      .expand-icon {
        transition: transform 0.3s ease;
        .menu-collapse__link--active & {
          transform: rotate(180deg);
        }
      }
    `
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MenuItemVerticalComponent {
  private readonly authService = inject(AuthenticationService);
  private readonly menuStateService = inject(MenuStateService);
  private readonly router = inject(Router);
  readonly internalRoutes = MENU_INTERNAL_ROUTES;
  readonly item = input.required<NavigationItem>();
  readonly level = input(0);
  readonly menuId = computed(() => this.item().id);
  readonly hasChildren = computed(() => (this.item().children ?? []).length > 0);
  readonly isEnabled = computed(() => {
    const menuItem = this.item();
    const userRole = this.authService.currentUser()?.user.role;
    if (!userRole || !menuItem.role?.length) {
      return true;
    }
    return menuItem.role.includes(userRole);
  });

  readonly isTrulyInternal = computed(() => {
    const id = this.item().id;
    return id in this.internalRoutes;
  });

  readonly isActive = computed(() => this.menuStateService.isRouteActive()(this.menuId()));
  readonly isOpen = computed(() => this.menuStateService.isMenuOpen()(this.menuId()));

  readonly iconName = computed(() => {
    const menuItem = this.item();
    const title = menuItem.title;
    if (title && MENU_ICON_MAP[title]) {
      return MENU_ICON_MAP[title];
    }
    const iconPath = menuItem.icon;
    if (iconPath) {
      if (iconPath.startsWith('fa fa-')) return iconPath.substring(6).trim();
      if (iconPath.startsWith('#custom-')) return iconPath.substring(8).trim();
      const parts = iconPath.split('/');
      const filename = parts.pop() || '';
      const dotIndex = filename.lastIndexOf('.');
      return dotIndex > -1 ? filename.substring(0, dotIndex) : filename;
    }
    return '';
  });

  readonly tooltipText = computed(() => (this.isEnabled() ? '' : 'No tienes permiso para acceder a esta función.'));

  handleClick(event: MouseEvent): void {
    const menuItem = this.item();

    if (!this.isEnabled()) {
      event.preventDefault();
      return;
    }

    if (this.hasChildren()) {
      event.preventDefault();
      this.menuStateService.toggleMenu(this.menuId());
      return;
    }

    if (this.isTrulyInternal()) {
      return;
    }

    if (!this.isTrulyInternal() && menuItem.url) {
      event.preventDefault();

      if (menuItem.target) {
        window.open(menuItem.url, '_blank');
      } else {
        window.location.href = menuItem.url;
      }
    }
  }

  onAddClick(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const addUrl = this.item().addUrl;
    if (addUrl && this.isEnabled()) {
      if (addUrl.startsWith('/')) {
        this.router.navigateByUrl(addUrl);
      } else {
        window.open(addUrl, '_blank');
      }
    }
  }
}
